import { useCanvasStore, type Item } from '@/stores/canvasStore';
import { useArrowStore } from '@/stores/arrowStore';
import { usePartnrConfirmStore } from '@/stores/partnrConfirmStore';
import { recordDeleteWithUndo } from '@/hooks/useUndoRedo';

function linkedBoardId(item: Item): string | undefined {
  return (item.content as { linked_board_id?: string } | null)?.linked_board_id;
}

function folderLabel(folder: Item): string {
  const { boards, allItems } = useCanvasStore.getState();
  const boardId = linkedBoardId(folder);
  const name = boards.find((b) => b.id === boardId)?.name || 'Untitled folder';
  const count = boardId ? allItems.filter((i) => i.board_id === boardId).length : 0;
  return count > 0 ? `${name} (${count} item${count === 1 ? '' : 's'} inside)` : `${name} (empty)`;
}

/**
 * Delete canvas items from a selection. Cards get an Undo toast. Folders
 * can't be undone (deleting one deletes its board, sub-folders and
 * everything in them), so a selection with folders asks first and only the
 * other cards go into the undo history.
 */
export async function deleteSelection(ids: string[]): Promise<void> {
  const { items } = useCanvasStore.getState();
  const targets = items.filter((i) => ids.includes(i.id));
  const folders = targets.filter((i) => i.item_type === 'board_link');
  const cards = targets.filter((i) => i.item_type !== 'board_link');
  if (targets.length === 0) return;

  if (folders.length > 0) {
    const folderWord = `folder${folders.length === 1 ? '' : 's'}`;
    const cardsPart = cards.length > 0 ? ` and ${cards.length} item${cards.length === 1 ? '' : 's'}` : '';
    const confirmed = await usePartnrConfirmStore.getState().ask({
      title: `Delete ${folders.length} ${folderWord}${cardsPart}?`,
      description:
        `Deleting a folder also deletes everything inside it, and that can't be undone.` +
        (cards.length > 0 ? ' The other items can still be restored with Undo.' : ''),
      items: folders.map(folderLabel),
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
  }

  const { deleteArrowsByItem } = useArrowStore.getState();
  for (const item of targets) deleteArrowsByItem(item.id);

  const store = useCanvasStore.getState();
  if (cards.length > 0) {
    // Snapshots before deletion, for undo
    recordDeleteWithUndo(cards);
    await store.deleteItems(cards.map((i) => i.id));
  }
  for (const folder of folders) {
    const boardId = linkedBoardId(folder);
    if (boardId) await useCanvasStore.getState().deleteBoard(boardId);
    else await useCanvasStore.getState().deleteItem(folder.id);
  }
  if (folders.length > 0) {
    const gone = new Set(folders.map((f) => f.id));
    useCanvasStore.setState((s) => ({
      selectedItemIds: new Set([...s.selectedItemIds].filter((id) => !gone.has(id))),
    }));
  }
}
